/**
 * OpsMate Context 定义与 Hook
 * 独立文件，确保 context 身份在 HMR 期间保持稳定
 */
import { createContext, useContext } from "react";
import type { ConversationsProps } from "@ant-design/x";
import type { GetProp } from "antd";
import type { MenuProps } from "antd/lib";

type MenuItem = Required<MenuProps>["items"][number];

export interface QuestionLibraryScope {
  docCategoryIds: string[];
  studySourceIds: string[];
  includePersonalDocs: boolean;
}

export interface OpsMateContextType {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  selectedKeys: string;
  setSelectedKeys: React.Dispatch<React.SetStateAction<string>>;
  resetConversation: () => void;
  askOpsConversations: GetProp<ConversationsProps, "items">;
  askOpsMateMenus: MenuItem[];
  openSearch: boolean;
  setOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
  pageHeight: string;
  historyOpsConversations: GetProp<ConversationsProps, "items">;
  fixFooterOpsConversations: GetProp<ConversationsProps, "items">;
  docsCollapsed: boolean;
  setDocsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  hasSelectedSources: boolean;
  setHasSelectedSources: React.Dispatch<React.SetStateAction<boolean>>;
  onlyUseSelectedSources: boolean;
  setOnlyUseSelectedSources: React.Dispatch<React.SetStateAction<boolean>>;
  /** 当前会话右侧文件清单的 checkbox 选中下标 */
  selectedSourceIndexes: Set<number>;
  setSelectedSourceIndexes: React.Dispatch<React.SetStateAction<Set<number>>>;
  items: any[];
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
  /** 按会话 ID 更新 items（流式回调使用，不受 selectedKeys 切换影响） */
  setItemsForConversation: (convId: string, updater: React.SetStateAction<any[]>) => void;
  manualSources: any[];
  setManualSources: React.Dispatch<React.SetStateAction<any[]>>;
  selectedSourceGuidNames: string[];
  setSelectedSourceGuidNames: React.Dispatch<React.SetStateAction<string[]>>;
  pendingConversationId: string | null;
  setPendingConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  /** 首问创建真实会话后，将 add 下的 Sources 与选择状态迁移到该会话 */
  migrateNewConversationSources: (conversationId: string) => void;
  refreshConversations: () => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  historyConversationsHasMore: boolean;
  historyConversationsLoadingMore: boolean;
  roles: (setItems: React.Dispatch<React.SetStateAction<any[]>>) => Record<string, any>;
  addSourcesVisible: boolean;
  setAddSourcesVisible: React.Dispatch<React.SetStateAction<boolean>>;
  /** 当前提问区选择的库范围，供 Regenerate 与普通提问复用同一请求契约。 */
  questionLibraryScope: QuestionLibraryScope;
  setQuestionLibraryScope: React.Dispatch<React.SetStateAction<QuestionLibraryScope>>;
  /** 最近一次回答所引用、且在 DB DocumentList 中存在的文档的“选中”行数（与右侧边栏选中数一致） */
  selectedSourcesCount: number;
  setSelectedSourcesCount: React.Dispatch<React.SetStateAction<number>>;
  availableSourcesCount: number;
  setAvailableSourcesCount: React.Dispatch<React.SetStateAction<number>>;
  listRef: React.RefObject<any>;
  aiReasoning: string;
  setAIReasoning: React.Dispatch<React.SetStateAction<string>>;
  aiReasoningVisible: boolean;
  setAIReasoningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  dislikeConfirmVisible: boolean;
  setDislikeConfirmVisible: React.Dispatch<React.SetStateAction<boolean>>;
  dislikeConfirmLoading: boolean;
  setDislikeConfirmLoading: React.Dispatch<React.SetStateAction<boolean>>;
  dislikeConfirmTarget: any;
  setDislikeConfirmTarget: React.Dispatch<React.SetStateAction<any>>;
  closeDislikeConfirm: () => void;
  confirmDislike: () => Promise<void>;
  reasoningCollapsedByKey: Record<string, boolean>;
  setReasoningCollapsedByKey: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  /** 重新生成 / 重试某条 AI 消息（传入该 AI 消息的 key） */
  regenerateMessage: (aiMessageKey: string) => Promise<void>;
  /** 当前高亮的文档标识（点击引用标记后设置，guid 优先、回退到文档名），"" 表示无高亮 */
  highlightedDocGuid: string;
  setHighlightedDocGuid: React.Dispatch<React.SetStateAction<string>>;
}

export const OpsMateContext = createContext<OpsMateContextType | null>(null);

export function useOpsMateContext() {
  const ctx = useContext(OpsMateContext);
  if (!ctx) throw new Error("useOpsMateContext must be used inside OpsMateProvider");
  return ctx;
}
